import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { joinRoom, getSocket } from './roomClient';
import { emptyVisit, fitCircleFromPoints, formatDart, scoreFromPoint, visitTotal } from './dartboardMath';
import { getCheckoutRoutes } from './checkout';
import { phoneRoomDraft, commitPhoneRoom } from './syncGuards';

const CORNER_LABELS = ['Top-left wire', 'Top-right wire', 'Bottom-right wire', 'Bottom-left wire'];

const GOLF_RESULTS = [
  { label: 'ALBATROSS', relativeScore: -3, color: '#6f42c1' },
  { label: 'EAGLE', relativeScore: -2, color: '#ffc107', text: '#000' },
  { label: 'BIRDIE', relativeScore: -1, color: '#28a745' },
  { label: 'PAR', relativeScore: 0, color: '#6c757d' },
  { label: 'BOGEY', relativeScore: 1, color: '#fd7e14' },
  { label: 'DBL BOGEY', relativeScore: 2, color: '#8b4513' },
  { label: 'TRIPLE', relativeScore: 3, color: '#dc3545' },
  { label: 'QUAD', relativeScore: 4, color: '#8b0000' },
];



const CAL_STORE = (room) => `astra_v2_cal_${String(room || 'HOME').toUpperCase()}`;
const PREFS_STORE = (room) => `astra_v2_cam_prefs_${String(room || 'HOME').toUpperCase()}`;

function loadCal(room) {
  try {
    const raw = localStorage.getItem(CAL_STORE(room));
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c && typeof c.cx === 'number' && typeof c.cy === 'number' && typeof c.r === 'number' && c.r > 0) return c;
  } catch (_) {}
  return null;
}

function saveCal(room, cal) {
  try {
    if (cal) localStorage.setItem(CAL_STORE(room), JSON.stringify(cal));
    else localStorage.removeItem(CAL_STORE(room));
  } catch (_) {}
}

function loadPrefs(room) {
  try {
    const raw = localStorage.getItem(PREFS_STORE(room));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function savePrefs(room, prefs) {
  try {
    localStorage.setItem(PREFS_STORE(room), JSON.stringify(prefs));
  } catch (_) {}
}

function haptic(pattern = 12) {
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern);
  } catch (_) {}
}

function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}


function sanitizeRoom(raw) {
  return commitPhoneRoom(raw);
}

function cameraLinkFor(room) {
  if (typeof window === 'undefined') return `/camera?room=${sanitizeRoom(room)}`;
  return `${window.location.origin}/camera?room=${sanitizeRoom(room)}`;
}

function isSecureEnough() {
  if (typeof window === 'undefined') return false;
  if (window.isSecureContext) return true;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

const CAMERA_HTTPS = 'https://astra-v2.170.64.239.122.sslip.io';

function cameraAccessErrorMessage(err) {
  const name = (err && err.name) || '';
  const msg = (err && err.message) || '';
  const here = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  if (!isSecureEnough() || name === 'SecurityError') {
    return `Camera blocked — this page is not HTTPS (${here || 'unknown'}). Open ${CAMERA_HTTPS}/camera?room=HOME (Safari on iPhone). Do not use http://IP:4002.`;
  }
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Camera permission denied. iPhone: Settings → Safari → Camera → Allow, then reload this page. Android: lock icon → Permissions → Camera → Allow, then tap Start camera again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No camera found on this phone. Unblock the camera in system settings, then tap Start camera again.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return 'Camera is in use or failed to start. Close other apps using the camera, then tap Start camera again.';
  }
  if (name === 'OverconstrainedError') {
    return 'This phone camera rejected the requested video mode. Tap Start camera again. If it keeps failing, try Safari (iPhone) or Chrome (Android).';
  }
  if (!err) return 'Could not open the camera.';
  return `Could not open the camera${name ? ` (${name})` : ''}${msg ? `: ${msg}` : ''}. Allow camera permission and use the HTTPS link.`;
}

function canvasFrameJpeg(video, quality = 0.72) {
  if (!video || !video.videoWidth) return null;
  const c = document.createElement('canvas');
  c.width = video.videoWidth;
  c.height = video.videoHeight;
  const ctx = c.getContext('2d');
  ctx.drawImage(video, 0, 0);
  return c.toDataURL('image/jpeg', quality);
}


const DEBUG_STORE = 'astra_v2_cam_debug_v1';

/** Keep last baseline+frame locally on the phone (IndexedDB). Never uploaded. */
async function saveDebugPair(entry) {
  const stamped = { ...entry, savedAt: Date.now() };
  // Keep memory in sync with IDB so Download never prefers a stale in-memory pair.
  try {
    if (typeof indexedDB === 'undefined') {
      saveDebugPair._mem = stamped;
      return { ok: true, via: 'memory' };
    }
    await new Promise((resolve, reject) => {
      const req = indexedDB.open(DEBUG_STORE, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('pairs')) db.createObjectStore('pairs', { keyPath: 'id' });
      };
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const db = req.result;
        const tx = db.transaction('pairs', 'readwrite');
        tx.objectStore('pairs').put({ id: 'last', ...stamped });
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      };
    });
    saveDebugPair._mem = stamped;
    return { ok: true, via: 'idb' };
  } catch (e) {
    saveDebugPair._mem = stamped;
    return { ok: false, error: String(e && e.message || e), via: 'memory' };
  }
}

async function loadDebugPair() {
  try {
    if (typeof indexedDB !== 'undefined') {
      const fromIdb = await new Promise((resolve, reject) => {
        const req = indexedDB.open(DEBUG_STORE, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains('pairs')) db.createObjectStore('pairs', { keyPath: 'id' });
        };
        req.onerror = () => reject(req.error);
        req.onsuccess = () => {
          const db = req.result;
          const tx = db.transaction('pairs', 'readonly');
          const g = tx.objectStore('pairs').get('last');
          g.onsuccess = () => resolve(g.result || null);
          g.onerror = () => reject(g.error);
        };
      });
      if (fromIdb && (fromIdb.baseline || fromIdb.frame)) {
        saveDebugPair._mem = fromIdb;
        return fromIdb;
      }
    }
  } catch (_) { /* fall through */ }
  return saveDebugPair._mem || null;
}

function downloadDataUrl(dataUrl, filename) {
  if (!dataUrl) return;
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Safari often swallows rapid multi-file downloads — stagger them. */
function downloadDataUrlStaggered(items, gapMs = 450) {
  items.forEach((it, i) => {
    setTimeout(() => downloadDataUrl(it.dataUrl, it.filename), i * gapMs);
  });
}


/** Map a scored dart tip into Astra Shanghai kind for the current round. */
export function mapDartToShanghaiKind(dart, round) {
  if (!dart || dart.miss) return 'M';
  if (!round || !round.type) return 'M';
  if (round.type === 'num') {
    const n = parseInt(round.label, 10) || 0;
    // Bulls never count on numbered Shanghai rounds
    if (dart.ring === 'singleBull' || dart.ring === 'doubleBull') return 'M';
    if (dart.segment !== n) return 'M';
    if (dart.ring === 'triple') return 'T';
    if (dart.ring === 'double') return 'D';
    if (dart.ring === 'single') return 'S';
    return 'M';
  }
  if (round.type === 'outer') {
    if (dart.ring === 'doubleBull') return 'B';
    if (dart.ring === 'singleBull') return 'O';
    return 'M';
  }
  if (round.type === 'bull') {
    return dart.ring === 'doubleBull' ? 'B' : 'M';
  }
  if (round.type === 'anyD') {
    return dart.ring === 'double' ? `D${dart.segment}` : 'M';
  }
  if (round.type === 'anyT') {
    return dart.ring === 'triple' ? `T${dart.segment}` : 'M';
  }
  return 'M';
}

function btn(bg) {
  return {
    padding: '12px 14px',
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
  };
}

export default function CameraCompanion() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [roomId, setRoomId] = useState(sanitizeRoom(params.get('room') || 'HOME'));
  const [joinedRoom, setJoinedRoom] = useState(() => sanitizeRoom(params.get('room') || 'HOME'));
  const [room, setRoom] = useState(null);
  const [error, setError] = useState('');
  const [camError, setCamError] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [calPoints, setCalPoints] = useState([]);
  const [calibration, setCalibration] = useState(null);
  const [calibrating, setCalibrating] = useState(true);
  const [visit, setVisit] = useState(emptyVisit());
  const [editIndex, setEditIndex] = useState(0);
  const [status, setStatus] = useState('Join a room, then start the camera.');
  const [autoDetect, setAutoDetect] = useState(true);
  const [detectBusy, setDetectBusy] = useState(false);
  const [detectInfo, setDetectInfo] = useState('Detector: idle');
  const [engineLabel, setEngineLabel] = useState('—');
  const [baselineDataUrl, setBaselineDataUrl] = useState(null);
  const [lastMotion, setLastMotion] = useState(0);
  const [step, setStep] = useState(1); // onboarding step hint
  const initialPrefs = useMemo(() => loadPrefs(sanitizeRoom(params.get('room') || 'HOME')) || {}, []);
  const [prefer, setPrefer] = useState(initialPrefs.prefer || 'auto');
  const [detectorHealth, setDetectorHealth] = useState(null);
  const [autoBaseline, setAutoBaseline] = useState(initialPrefs.autoBaseline !== false);
  const [hapticsOn, setHapticsOn] = useState(initialPrefs.hapticsOn !== false);
  const [showIosTips, setShowIosTips] = useState(false);
  const [calSavedNote, setCalSavedNote] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [golfBusy, setGolfBusy] = useState(false);
  const [socketOk, setSocketOk] = useState(false);
  const [presence, setPresence] = useState({ cameraConnected: false, cameraCount: 0, scoreboardCount: 0 });
  const [wizardOpen, setWizardOpen] = useState(() => initialPrefs.wizardDone !== true);
  const [shareNote, setShareNote] = useState('');
  const [practiceOn, setPracticeOn] = useState(false);
  const [practiceStats, setPracticeStats] = useState({ throws: 0, db: 0, sb: 0, miss: 0 });
  const [detectTimedOut, setDetectTimedOut] = useState(false);
  const [rejectShaky, setRejectShaky] = useState(initialPrefs.rejectShaky !== false);
  const rejectShakyRef = useRef(true);
  const lastMotionRef = useRef(0);
  const [lastDiag, setLastDiag] = useState(null); // {count, confs, engine, note}
  const [captureDebug, setCaptureDebug] = useState(initialPrefs.captureDebug === true);
  const [debugNote, setDebugNote] = useState('');
  const captureDebugRef = useRef(false);
  const autoBaselineRef = useRef(true);
  const hapticsRef = useRef(true);
  const confirmBusyRef = useRef(false);
  const confirmTimerRef = useRef(null);
  const golfBusyRef = useRef(false);
  const golfBusyTimerRef = useRef(null);
  const joinedRoomRef = useRef(null);
  const detectBusyRef = useRef(false);
  const detectAbortRef = useRef(null);
  const wakeLockRef = useRef(null);
  const practiceOnRef = useRef(false);
  const calPushRef = useRef(''); // roomId last pushed to server

  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const baselineRef = useRef(null);
  const visitRef = useRef(visit);
  const calRef = useRef(calibration);
  const autoRef = useRef(autoDetect);
  const settleTimer = useRef(null);
  const prevGrayRef = useRef(null);
  const loopRef = useRef(null);
  const lastDetectAt = useRef(0);

  useEffect(() => { visitRef.current = visit; }, [visit]);
  useEffect(() => { calRef.current = calibration; }, [calibration]);
  useEffect(() => { autoRef.current = autoDetect; }, [autoDetect]);
  useEffect(() => { autoBaselineRef.current = autoBaseline; }, [autoBaseline]);
  useEffect(() => { rejectShakyRef.current = rejectShaky; }, [rejectShaky]);
  useEffect(() => { captureDebugRef.current = captureDebug; }, [captureDebug]);
  useEffect(() => { lastMotionRef.current = lastMotion; }, [lastMotion]);
  useEffect(() => { hapticsRef.current = hapticsOn; }, [hapticsOn]);
  useEffect(() => { practiceOnRef.current = practiceOn; }, [practiceOn]);
  useEffect(() => { baselineRef.current = baselineDataUrl; }, [baselineDataUrl]);
  useEffect(() => { joinedRoomRef.current = joinedRoom; }, [joinedRoom]);
  useEffect(() => {
    savePrefs(joinedRoom || roomId, { prefer, autoBaseline, hapticsOn, autoDetect, rejectShaky, captureDebug, wizardDone: !wizardOpen });
  }, [joinedRoom, roomId, prefer, autoBaseline, hapticsOn, autoDetect, rejectShaky, captureDebug, wizardOpen]);

  // Restore per-room calibration from phone storage (survives refresh / detector restarts).
  // Clear when switching to a room with no saved cal (avoid leaking HOME cal into another room).
  useEffect(() => {
    const rid = sanitizeRoom(joinedRoom || roomId);
    calPushRef.current = '';
    const local = loadCal(rid);
    if (local) {
      setCalibration(local);
      setCalibrating(false);
      setCalPoints([]);
      setCalSavedNote(`Saved calibration for room ${rid} restored`);
      setStep((prev) => Math.max(prev, 3));
      setStatus(`Calibration restored for room ${rid}. Start camera → Capture baseline (or auto).`);
    } else {
      setCalibration(null);
      setCalibrating(true);
      setCalPoints([]);
      setCalSavedNote('');
      setBaselineDataUrl(null);
      baselineRef.current = null;
      setStep(1);
    }
  }, [joinedRoom]);

  useEffect(() => {
    const s = joinRoom(joinedRoom || roomId, 'camera');
    const refreshBaselineSoon = () => {
      let attempts = 0;
      const tryCapture = () => {
        attempts += 1;
        if (!autoBaselineRef.current || !videoRef.current || !streamRef.current) return;
        // Wait until frame is relatively still (hand/pull out of view)
        if (lastMotionRef.current > 1800 && attempts < 8) {
          setDetectInfo(`Waiting for steady frame before baseline… (motion ${lastMotionRef.current})`);
          setTimeout(tryCapture, 450);
          return;
        }
        const data = canvasFrameJpeg(videoRef.current, 0.75);
        if (!data) return;
        setBaselineDataUrl(data);
        baselineRef.current = data;
        setStatus(attempts > 1
          ? 'Auto baseline refreshed (waited for steady frame). Ready for next visit.'
          : 'Auto baseline refreshed after confirm — board should be clear / after pull.');
        setDetectInfo('Baseline ready (auto)');
        setStep(4);
      };
      setTimeout(tryCapture, 1200);
    };
    const onState = (st) => {
      setRoom(st);
      if (st.presence) setPresence(st.presence);
      if (st.visit) setVisit(st.visit.map((d) => (d ? { ...d } : null)));
      if (st.calibration && st.calibration.r) {
        // Prefer existing phone cal; otherwise adopt room cal and cache locally
        setCalibration((prev) => {
          if (prev && prev.r) return prev;
          saveCal(joinedRoomRef.current || roomId, st.calibration);
          setCalSavedNote(`Room calibration for ${joinedRoomRef.current || roomId} loaded`);
          return st.calibration;
        });
        setCalibrating(false);
        setStep((s) => Math.max(s, 3));
      } else if (calRef.current && calRef.current.r) {
        // Phone has cal, server room does not yet — push once per room so iPad/server remember it
        const rid = joinedRoomRef.current || roomId;
        if (calPushRef.current !== rid) {
          calPushRef.current = rid;
          try {
            getSocket().emit('calibration:save', {
              roomId: rid,
              calibration: calRef.current,
            });
          } catch (_) {}
        }
      }
    };
    const onPresence = (p) => setPresence(p || { cameraConnected: false, cameraCount: 0, scoreboardCount: 0 });
    const clearGolfBusy = () => {
      golfBusyRef.current = false;
      setGolfBusy(false);
      if (golfBusyTimerRef.current) {
        clearTimeout(golfBusyTimerRef.current);
        golfBusyTimerRef.current = null;
      }
    };
    const onErr = (e) => {
      confirmBusyRef.current = false;
      setConfirmBusy(false);
      if (confirmTimerRef.current) { clearTimeout(confirmTimerRef.current); confirmTimerRef.current = null; }
      clearGolfBusy();
      setError(e.error || 'Room error');
    };
    const onApplied = (r) => {
      confirmBusyRef.current = false;
      setConfirmBusy(false);
      if (confirmTimerRef.current) { clearTimeout(confirmTimerRef.current); confirmTimerRef.current = null; }
      if (hapticsRef.current) haptic([10, 40, 10]);
      if (r.win) setStatus(`Match shot! ${r.winner} wins.`);
      else if (r.bust) setStatus('Bust — turn passed. Pull darts, then baseline refreshes.');
      else setStatus('501 visit confirmed on iPad. Pull darts — baseline will refresh.');
      setVisit(emptyVisit());
      setEditIndex(0);
      setBaselineDataUrl(null);
      baselineRef.current = null;
      setStep(3);
      refreshBaselineSoon();
    };
    const onShApplied = () => {
      confirmBusyRef.current = false;
      setConfirmBusy(false);
      if (confirmTimerRef.current) { clearTimeout(confirmTimerRef.current); confirmTimerRef.current = null; }
      if (hapticsRef.current) haptic([10, 40, 10]);
      setStatus('Shanghai visit confirmed on iPad. Pull darts — baseline will refresh.');
      setVisit(emptyVisit());
      setEditIndex(0);
      setBaselineDataUrl(null);
      baselineRef.current = null;
      setStep(3);
      refreshBaselineSoon();
    };
    const onGolfApplied = (payload) => {
      clearGolfBusy();
      if (hapticsRef.current) haptic([10, 40, 10]);
      const rel = payload && Number(payload.relativeScore);
      const label = Number.isFinite(rel)
        ? (GOLF_RESULTS.find((g) => g.relativeScore === rel)?.label || String(rel))
        : 'result';
      setStatus(`Golf ack on iPad · ${label}${payload && payload.player ? ` · ${payload.player}` : ''}.`);
    };
    const onConnect = () => {
      setSocketOk(true);
      setError('');
      setStatus((prev) => (prev && (prev.includes('reconnect') || prev.includes('Reconnect') || prev.includes('Link dropped'))
        ? `Rejoined room ${joinedRoomRef.current || roomId}`
        : prev));
      const rid = joinedRoomRef.current || roomId;
      const local = loadCal(rid);
      if (local && local.r) {
        try {
          getSocket().emit('calibration:save', { roomId: rid, calibration: local });
        } catch (_) {}
      }
      if (hapticsRef.current) haptic(10);
    };
    const onDisconnect = (reason) => {
      setSocketOk(false);
      setStatus(`Link dropped (${reason || 'network'}) — reconnecting…`);
    };
    const onErrSock = () => {
      setSocketOk(false);
      setStatus('Link error — reconnecting…');
    };
    s.on('room:state', onState);
    s.on('room:presence', onPresence);
    s.on('room:error', onErr);
    s.on('visit:applied', onApplied);
    s.on('shanghai:cameraConfirm', onShApplied);
    s.on('golf:cameraConfirm', onGolfApplied);
    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('connect_error', onErrSock);
    setSocketOk(!!s.connected);
    return () => {
      s.off('room:state', onState);
      s.off('room:presence', onPresence);
      s.off('room:error', onErr);
      s.off('visit:applied', onApplied);
      s.off('shanghai:cameraConfirm', onShApplied);
      s.off('golf:cameraConfirm', onGolfApplied);
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off('connect_error', onErrSock);
    };
  }, [joinedRoom]);

  useEffect(() => {
    fetch('/api/detect/health')
      .then((r) => r.json())
      .then((d) => setDetectorHealth(d))
      .catch(() => setDetectorHealth({ ok: false }));
  }, []);

  const reconnectRoom = () => {
    const id = sanitizeRoom(roomId);
    setRoomId(id);
    setJoinedRoom(id);
    setError('');
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('room', id);
      window.history.replaceState({}, '', u.toString());
    } catch (_) {}
    joinRoom(id, 'camera');
    setStatus(`Joining room ${id}…`);
    setStep((s) => Math.max(s, 1));
    // Confirm link after a beat
    setTimeout(() => {
      const ok = !!(getSocket() && getSocket().connected);
      setSocketOk(ok);
      setStatus(ok ? `Joined room ${id}` : `Joining room ${id} — waiting for link…`);
      if (ok && hapticsRef.current) haptic(12);
      // Re-push cal for this room after join
      const local = loadCal(id);
      if (local && local.r) {
        try { getSocket().emit('calibration:save', { roomId: id, calibration: local }); } catch (_) {}
      }
    }, 400);
  };

  const dismissWizard = (permanent) => {
    setWizardOpen(false);
    if (permanent) {
      savePrefs(sanitizeRoom(roomId), {
        prefer, autoBaseline, hapticsOn, autoDetect, rejectShaky, wizardDone: true,
      });
    }
  };

  const shareCameraLink = async () => {
    const url = cameraLinkFor(joinedRoom || roomId);
    setShareNote('');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Astra Darts camera', text: `Room ${sanitizeRoom(joinedRoom || roomId)}`, url });
        setShareNote('Shared');
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setShareNote('Link copied');
      } else {
        window.prompt('Copy camera link', url);
        setShareNote('Copy from prompt');
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(url);
        setShareNote('Link copied');
      } catch (_) {
        window.prompt('Copy camera link', url);
      }
    }
    setTimeout(() => setShareNote(''), 2500);
  };

  const recordPractice = (dart) => {
    if (!practiceOnRef.current || !dart) return;
    setPracticeStats((s) => {
      const next = { ...s, throws: s.throws + 1 };
      if (dart.ring === 'doubleBull') next.db += 1;
      else if (dart.ring === 'singleBull') next.sb += 1;
      else next.miss += 1;
      return next;
    });
  };

  const requestWakeLock = async () => {
    try {
      if (wakeLockRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.wakeLock && navigator.wakeLock.request) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => { wakeLockRef.current = null; });
      }
    } catch (_) {}
  };

  const releaseWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (_) {}
  };

  const stopCamera = useCallback(() => {
    if (loopRef.current) {
      clearInterval(loopRef.current);
      loopRef.current = null;
    }
    if (settleTimer.current) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
    if (detectAbortRef.current) {
      try { detectAbortRef.current.abort(); } catch (_) {}
      detectAbortRef.current = null;
    }
    detectBusyRef.current = false;
    setDetectBusy(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreaming(false);
    releaseWakeLock();
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const emitDart = (index, dart) => {
    const next = [...visitRef.current];
    next[index] = dart;
    setVisit(next);
    visitRef.current = next;
    recordPractice(dart);
    getSocket().emit('visit:setDart', {
      roomId: joinedRoom || roomId,
      index,
      dart,
      source: dart.source || 'tap',
    });
  };

  const captureBaseline = () => {
    const data = canvasFrameJpeg(videoRef.current);
    if (!data) {
      setStatus('Start the camera before capturing baseline.');
      return;
    }
    setBaselineDataUrl(data);
    baselineRef.current = data;
    if (hapticsRef.current) haptic([8, 20, 8]);
    setStatus('Baseline locked (empty board / after pull). Throw, then wait for auto-detect — or tap board.');
    setDetectInfo('Baseline ready');
    setStep(4);
  };

  const runDetect = useCallback(async () => {
    if (detectBusyRef.current) return;
    const now = Date.now();
    if (now - lastDetectAt.current < 700) return; // debounce
    lastDetectAt.current = now;
    const cal = calRef.current;
    if (!cal || !videoRef.current) return;
    // Client-side reject shaky / high-motion frames before hitting detector
    if (rejectShakyRef.current && lastMotionRef.current > 3200) {
      setDetectInfo(`Skipped shaky frame (motion ${lastMotionRef.current}) — hold still, retry`);
      setLastDiag({ count: 0, confs: [], engine: 'none', note: 'client-reject-shaky' });
      return;
    }
    const image = canvasFrameJpeg(videoRef.current, 0.72);
    if (!image) return;
    detectBusyRef.current = true;
    setDetectBusy(true);
    setDetectTimedOut(false);
    if (detectAbortRef.current) {
      try { detectAbortRef.current.abort(); } catch (_) {}
    }
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    detectAbortRef.current = ac;
    const timer = setTimeout(() => {
      try { ac && ac.abort(); } catch (_) {}
    }, 12000);
    try {
      const knownTips = (visitRef.current || [])
        .filter((d) => d && d.x != null)
        .map((d) => ({ x: d.x, y: d.y }));
      const res = await fetch('/api/detect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image,
          baseline: baselineRef.current || undefined,
          calibration: cal,
          knownTips,
          prefer,
          minConfidence: 0.40,
          rejectShaky: rejectShakyRef.current,
        }),
        signal: ac ? ac.signal : undefined,
      });
      const data = await res.json();
      if (!data.ok) {
        setDetectInfo(`Detect fail: ${data.error || 'unknown'} — tap overlay still works`);
        setEngineLabel(data.engine || 'none');
        return;
      }
      setEngineLabel(data.engine || 'opencv');
      const darts = data.darts || [];
      const confs = (data.diag && data.diag.confidences) || darts.map((d) => d.confidence);
      const rejected = data.diag && data.diag.rejected;
      const rawListEarly = (data.diag && (data.diag.rawConfidences
        || (data.diag.onnx && data.diag.onnx.rawConfidences))) || [];
      setLastDiag({
        count: darts.length,
        confs: confs || [],
        rawConfs: rawListEarly || [],
        engine: data.engine || 'none',
        note: rejected ? (data.diag.reason || 'rejected') : ((data.notes || [])[0] || 'ok'),
      });
      if (rejected) {
        setDetectInfo(`Rejected: ${data.diag.reason || 'shaky/lighting'} — hold still & Detect again`);
        if (captureDebugRef.current) {
          saveDebugPair({
            baseline: baselineRef.current || null,
            frame: image,
            calibration: cal,
            engine: 'none',
            darts: [],
            diag: data.diag || null,
            notes: data.notes || [],
            rejected: true,
            roomId: joinedRoom || roomId,
          }).then((r) => setDebugNote(r && r.ok ? `Debug (rejected) saved (${r.via})` : 'Debug save memory'))
            .catch(() => {});
        }
        return;
      }
      const confStr = (confs || []).map((c) => (typeof c === 'number' ? c.toFixed(2) : c)).join(',') || '—';
      const rawList = (data.diag && (data.diag.rawConfidences
        || (data.diag.onnx && data.diag.onnx.rawConfidences))) || [];
      const rawStr = (rawList || []).map((c) => (typeof c === 'number' ? c.toFixed(3) : c)).join(', ') || '';
      setDetectInfo(`${data.engine}: ${darts.length} tip(s) · calibrated ${confStr}${rawStr ? ` · raw ${rawStr}` : ''} · ${(data.notes || []).join(' · ') || 'ok'}`);
      if (captureDebugRef.current) {
        saveDebugPair({
          baseline: baselineRef.current || null,
          frame: image,
          calibration: cal,
          engine: data.engine,
          darts,
          diag: data.diag || null,
          notes: data.notes || [],
          roomId: joinedRoom || roomId,
        }).then((r) => setDebugNote(r && r.ok ? `Debug pair saved locally (${r.via})` : `Debug save: ${(r && r.error) || 'memory'}`))
          .catch(() => setDebugNote('Debug save failed'));
      }
      if (!darts.length) return;

      let next = [...visitRef.current];
      let filled = 0;
      let skippedLow = 0;
      // Align with detector minConfidence (0.40). Gate on calibrated confidence, never raw.
      const FILL_MIN = 0.40;
      for (const d of darts) {
        const conf = Number(d && d.confidence);
        if (!Number.isFinite(conf) || conf < FILL_MIN) {
          skippedLow += 1;
          continue;
        }
        const clash = next.some(
          (ex) => ex && ex.x != null && Math.hypot(ex.x - d.x, ex.y - d.y) < (cal.r || 100) * 0.05
        );
        if (clash) continue;
        const emptyIdx = next.findIndex((x) => !x);
        if (emptyIdx < 0) break;
        const dart = { ...d, source: 'cv' };
        next[emptyIdx] = dart;
        recordPractice(dart);
        getSocket().emit('visit:setDart', {
          roomId: joinedRoom || roomId,
          index: emptyIdx,
          dart,
          source: 'cv',
        });
        filled += 1;
      }
      if (filled) {
        setVisit(next);
        visitRef.current = next;
        const nextEdit = next.findIndex((x) => !x);
        setEditIndex(nextEdit >= 0 ? nextEdit : 2);
        if (hapticsRef.current) haptic([14, 28, 14]);
        setStatus(`Auto-filled ${filled} dart(s). 2-tap fix if wrong, then Confirm turn.`);
        setStep(5);
      } else if (skippedLow) {
        setStatus(`Detected ${darts.length} tip(s) but conf < ${FILL_MIN.toFixed(2)} — tap overlay / 2-tap, then Confirm.`);
      }
    } catch (e) {
      const aborted = e && (e.name === 'AbortError' || /aborted/i.test(String(e.message || '')));
      if (aborted) {
        setDetectTimedOut(true);
        setDetectInfo('Detect timed out (12s) — tap overlay still works. Try Detect now again.');
        setStatus('Detector slow / busy — use tap overlay or retry Detect.');
      } else {
        setDetectInfo(`Detect error: ${e.message || e} — use tap overlay`);
      }
    } finally {
      clearTimeout(timer);
      detectBusyRef.current = false;
      setDetectBusy(false);
      if (detectAbortRef.current === ac) detectAbortRef.current = null;
    }
  }, [joinedRoom, roomId, prefer, rejectShaky]);

  const startMotionLoop = () => {
    if (loopRef.current) clearInterval(loopRef.current);
    loopRef.current = setInterval(() => {
      if (!autoRef.current || !calRef.current || !videoRef.current || !baselineRef.current) return;
      const video = videoRef.current;
      if (!video.videoWidth) return;
      const c = document.createElement('canvas');
      const w = 160;
      const h = Math.round((video.videoHeight / video.videoWidth) * w) || 90;
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      ctx.drawImage(video, 0, 0, w, h);
      const frame = ctx.getImageData(0, 0, w, h).data;
      let energy = 0;
      if (prevGrayRef.current && prevGrayRef.current.length === frame.length) {
        for (let i = 0; i < frame.length; i += 16) {
          const g1 = (frame[i] + frame[i + 1] + frame[i + 2]) / 3;
          const g0 = (prevGrayRef.current[i] + prevGrayRef.current[i + 1] + prevGrayRef.current[i + 2]) / 3;
          energy += Math.abs(g1 - g0);
        }
      }
      prevGrayRef.current = frame;
      const motionRounded = Math.round(energy);
      lastMotionRef.current = motionRounded; // sync immediately (do not wait for React effect)
      setLastMotion(motionRounded);
      const motionHigh = energy > 2500;
      if (motionHigh) {
        if (settleTimer.current) clearTimeout(settleTimer.current);
        settleTimer.current = setTimeout(() => {
          if (autoRef.current) runDetect();
        }, 950);
      }
    }, 400);
  };

  const startCamera = async () => {
    setCamError('');
    if (!isSecureEnough()) {
      const here = window.location.origin;
      setCamError(
        `Camera blocked — ${here} is not a secure page. Phones only allow the camera on HTTPS. Open ${CAMERA_HTTPS}/camera?room=${sanitizeRoom(joinedRoom || roomId)} in Safari (iPhone) or Chrome (Android). Not http://…:4002.`
      );
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCamError(`This browser has no camera API. Use Safari on iPhone or Chrome on Android, on ${CAMERA_HTTPS}.`);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStreaming(true);
      if (hapticsRef.current) haptic(10);
      if (calRef.current && calRef.current.r) {
        setCalibrating(false);
        setStatus('Camera live. Calibration already saved — Capture baseline (clear board), then throw.');
        setStep(3);
      } else {
        setCalibrating(true);
        setStatus('Camera live. Calibrate 4 corners, Capture baseline, then throw.');
        setStep(2);
      }
      startMotionLoop();
      requestWakeLock();
    } catch (e) {
      setCamError(cameraAccessErrorMessage(e));
    }
  };

  const videoPointFromEvent = (evt) => {
    const video = videoRef.current;
    const overlay = overlayRef.current;
    if (!video || !overlay) return null;
    const rect = overlay.getBoundingClientRect();
    const x = ((evt.clientX - rect.left) / rect.width) * video.videoWidth;
    const y = ((evt.clientY - rect.top) / rect.height) * video.videoHeight;
    if (!video.videoWidth) return null;
    return { x, y };
  };

  const onOverlayClick = (evt) => {
    const pt = videoPointFromEvent(evt);
    if (!pt) return;

    if (calibrating || !calibration) {
      const next = [...calPoints, pt];
      setCalPoints(next);
      if (next.length >= 4) {
        const cal = fitCircleFromPoints(next.slice(0, 4));
        setCalibration(cal);
        setCalibrating(false);
        setCalPoints([]);
        const rid = joinedRoom || roomId;
        saveCal(rid, cal);
        setCalSavedNote(`Calibration saved for room ${rid}`);
        getSocket().emit('calibration:save', { roomId: rid, calibration: cal });
        if (hapticsRef.current) haptic(20);
        setStatus('Board calibrated (saved on this phone). Capture baseline (clear board), then auto or tap darts.');
        setStep(3);
      } else {
        if (hapticsRef.current) haptic(8);
        setStatus(`Calibration: tap ${CORNER_LABELS[next.length] || 'next corner'} (${next.length}/4)`);
      }
      return;
    }

    const scored = scoreFromPoint(pt.x, pt.y, calibration);
    const dart = {
      ...scored,
      confidence: 1,
      source: 'tap',
      x: pt.x,
      y: pt.y,
    };
    const idx = editIndex;
    emitDart(idx, dart);
    if (hapticsRef.current) haptic(12);
    const nextIdx = Math.min(2, idx + 1);
    setEditIndex(nextIdx);
    setStatus(`Dart ${idx + 1}: ${formatDart(dart)} (${dart.value}). ${idx < 2 ? 'Tap next dart or Confirm.' : 'Confirm turn when ready.'}`);
    setStep(5);
  };

  const shanghaiKindsFromVisit = () => {
    const round = room?.shRound || { type: 'num', label: '20' };
    // Preserve throw order; skip empty slots (user may leave middle empty after 2-tap edits)
    return (visit || []).filter(Boolean).map((d) => mapDartToShanghaiKind(d, round));
  };

  const confirmVisit = () => {
    const mode = room?.mode;
    if (confirmBusyRef.current) {
      setStatus('Confirm already sent — wait for iPad…');
      return;
    }
    if (!socketOk) {
      setError('Not linked to iPad — wait for reconnect, then Confirm again.');
      return;
    }
    if (mode !== '501' && mode !== 'shanghai') {
      setError('Start 501 or Shanghai on the iPad first');
      return;
    }
    // 501 empty visit = intentional 0 (miss visit). Shanghai empty = miss/half.
    confirmBusyRef.current = true;
    setConfirmBusy(true);
    if (hapticsRef.current) haptic([18, 30, 18]);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = setTimeout(() => {
      confirmTimerRef.current = null;
      // safety unlock if server never answers
      if (confirmBusyRef.current) {
        confirmBusyRef.current = false;
        setConfirmBusy(false);
        setStatus('Confirm timed out — check iPad link, then try Confirm again.');
      }
    }, 6000);
    if (mode === 'shanghai') {
      const kinds = shanghaiKindsFromVisit();
      getSocket().emit('visit:confirm', {
        roomId: joinedRoom || roomId,
        visit,
        shanghaiKinds: kinds.length ? kinds : [],
      });
      setStatus(kinds.length ? `Shanghai confirm sent (${kinds.join(' · ')})…` : 'Shanghai miss/half sent…');
      return;
    }
    getSocket().emit('visit:confirm', {
      roomId: joinedRoom || roomId,
      visit,
    });
    const tot = visitTotal(visit);
    const n = (visit || []).filter(Boolean).length;
    setStatus(n ? `501 confirm sent · ${tot}…` : '501 confirm sent · 0 (empty visit)…');
  };

  const clearVisit = () => {
    setVisit(emptyVisit());
    setEditIndex(0);
    getSocket().emit('visit:clear', { roomId: joinedRoom || roomId });
  };

  const undoMatch = () => {
    getSocket().emit('match:undo', { roomId: joinedRoom || roomId });
    setStatus('Undo requested.');
  };

  const setMiss = () => {
    emitDart(editIndex, { label: 'M', value: 0, miss: true, confidence: 1, source: 'tap' });
    setEditIndex(Math.min(2, editIndex + 1));
  };

  const active501 = room?.p501?.[room?.active501Idx];
  const activeSh = room?.shPlayers?.[room?.activeShIdx];
  const total = visitTotal(visit);
  const secure = isSecureEnough();
  const mode = room?.mode;
  const canConfirm = mode === '501' || mode === 'shanghai';
  const shKindsPreview = mode === 'shanghai' ? shanghaiKindsFromVisit() : [];
  const onnxPresent = detectorHealth?.onnx?.present;
  const activeGolf = room?.golfPlayers?.[room?.activeGolfIdx];
  const golfPar = room?.golfPar ?? 4;
  const dartCount = (visit || []).filter(Boolean).length;
  const confirmLabel = mode === 'shanghai'
    ? (dartCount ? `Confirm Shanghai (${dartCount} dart${dartCount === 1 ? '' : 's'})` : 'Confirm Shanghai (miss / half)')
    : mode === '501'
      ? (dartCount ? `Confirm 501 visit · ${total}` : 'Confirm 501 visit')
      : 'Confirm turn';
  const confirmHint = mode === 'shanghai'
    ? 'Maps tips → S/D/T/M for the current target, then advances the iPad.'
    : mode === '501'
      ? 'Sends the three-dart visit to the iPad (bust / score / checkout).'
      : mode === 'golf'
        ? 'Golf uses the hole-result buttons below (camera tips are for 501 / Shanghai).'
        : 'Start 501, Shanghai, or Golf on the iPad first.';

  const confirmGolf = (relativeScore) => {
    if (mode !== 'golf') return;
    if (!socketOk) {
      setError('Not linked to iPad — wait for reconnect, then send again.');
      return;
    }
    if (golfBusyRef.current) {
      setStatus('Golf result already sent — wait for iPad…');
      return;
    }
    if (golfPar + relativeScore <= 0) {
      setStatus('That result is not valid for this par.');
      return;
    }
    golfBusyRef.current = true;
    setGolfBusy(true);
    if (golfBusyTimerRef.current) clearTimeout(golfBusyTimerRef.current);
    golfBusyTimerRef.current = setTimeout(() => {
      if (golfBusyRef.current) {
        golfBusyRef.current = false;
        setGolfBusy(false);
        setStatus('Golf confirm timed out — check iPad link, then try again.');
      }
      golfBusyTimerRef.current = null;
    }, 6000);
    getSocket().emit('golf:confirm', {
      roomId: joinedRoom || roomId,
      relativeScore,
    });
    if (hapticsRef.current) haptic([12, 24, 12]);
    setStatus(`Sent ${GOLF_RESULTS.find((g) => g.relativeScore === relativeScore)?.label || relativeScore} to iPad…`);
  };

  const steps = [
    { n: 1, t: 'Join room (same code as iPad)' },
    { n: 2, t: 'Start camera + tap 4 outer corners' },
    { n: 3, t: 'Capture baseline (board clear / after pull)' },
    { n: 4, t: 'Throw — wait for settle or Detect now' },
    { n: 5, t: '2-tap fix if needed → Confirm turn' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0b0f14', color: '#e8eef6', fontFamily: 'sans-serif', boxSizing: 'border-box', padding: '12px 12px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, color: '#7a8796', letterSpacing: 1 }}>ASTRA DARTS v2 · CAMERA · 9.4.5</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>Phone companion</div>
        </div>
        <a href="/" style={{ color: '#7db7ff', fontSize: 16 }}>← Scoreboard</a>
      </div>

      {/* Sticky test-day status strip */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 40,
          marginBottom: 10,
          padding: '10px 12px',
          borderRadius: 12,
          border: '1px solid #243044',
          background: 'rgba(12,18,28,0.96)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        <span style={{
          padding: '4px 10px', borderRadius: 999, background: '#1a2330', color: '#4dabf7',
          letterSpacing: 1,
        }}>ROOM {sanitizeRoom(joinedRoom || roomId)}</span>
        <span style={{ color: socketOk ? '#28a745' : '#dc3545' }}>
          {socketOk ? '● Linked' : '○ Reconnecting'}
        </span>
        <span style={{ color: presence.scoreboardCount ? '#28a745' : '#888' }}>
          iPad {presence.scoreboardCount ? 'connected' : 'not seen'}
        </span>
        <span style={{ color: streaming ? '#28a745' : '#888' }}>
          Cam {streaming ? 'on' : 'off'}
        </span>
        <span style={{ color: calibration && calibration.r ? '#28a745' : '#fd7e14' }}>
          Cal {calibration && calibration.r ? '✓' : 'needed'}
        </span>
        <span style={{ color: baselineDataUrl ? '#28a745' : '#fd7e14' }}>
          Baseline {baselineDataUrl ? 'ready' : 'needed'}
        </span>
        <button
          type="button"
          onClick={captureBaseline}
          disabled={!streaming}
          style={{
            marginLeft: 'auto',
            padding: '6px 10px',
            borderRadius: 999,
            border: '1px solid #1b4332',
            background: streaming ? '#0f3d32' : '#1a2330',
            color: streaming ? '#8fbc8f' : '#667',
            fontWeight: 800,
            fontSize: 12,
            cursor: streaming ? 'pointer' : 'not-allowed',
          }}
        >
          {baselineDataUrl ? 'Recapture baseline' : 'Capture baseline'}
        </button>
        {mode && mode !== 'menu' ? (
          <span style={{ color: '#9ec5fe' }}>{String(mode).toUpperCase()}</span>
        ) : (
          <span style={{ color: '#6c757d' }}>waiting for match</span>
        )}
      </div>

      <div style={{
        marginBottom: 10, padding: '10px 12px', borderRadius: 12,
        border: '1px solid #1b4332', background: 'linear-gradient(90deg,#0f2419,#122033)',
        fontSize: 13, color: '#b7e4c7', lineHeight: 1.4,
      }}>
        <strong style={{ color: '#74c69d' }}>Ready for home test · v9.4.5</strong>
        {' '}· HTTPS iPad + phone · Join HOME · Cal → Baseline → Detect → 2-tap fix → Confirm.
        Assist only (not DartsOn). Capture debug stays on this phone. Golf = hole buttons (wait for ack).
      </div>

      {wizardOpen && (
        <div style={{ background: 'linear-gradient(160deg,#132238,#0d1520)', border: '2px solid #2b6cb0', borderRadius: 14, padding: 16, marginBottom: 12, boxShadow: '0 10px 28px rgba(0,0,0,0.35)' }}>
          <div style={{ fontSize: 13, color: '#7db7ff', fontWeight: 800, letterSpacing: 1, marginBottom: 6 }}>FIRST-RUN WIZARD · STEP {Math.min(5, Math.max(1, step))} / 5</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#e8eef6', marginBottom: 8 }}>
            {step <= 1 && '1 · Join the same room as the iPad'}
            {step === 2 && '2 · Start camera & tap 4 outer corners'}
            {step === 3 && '3 · Capture baseline (clear board)'}
            {step === 4 && '4 · Throw — wait for settle or Detect'}
            {step >= 5 && '5 · 2-tap fix if needed → Confirm'}
          </div>
          <div style={{ fontSize: 14, color: '#9fb0c3', marginBottom: 12, lineHeight: 1.45 }}>
            {step <= 1 && 'Room code must match the iPad (default HOME). Tap Join, then Start camera.'}
            {step === 2 && 'Tripod phone so the whole scoring wire is visible. Tap the four outer corners in order.'}
            {step === 3 && 'Board empty (or after pull). Capture baseline before each visit if auto-baseline is off.'}
            {step === 4 && 'Throw, keep hands out of frame. Auto-detect after settle, or tap Detect now / tap the tip.'}
            {step >= 5 && 'Wrong tip? Tap a dart slot, then the correct spot (or Miss). Big green Confirm sends to the iPad.'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {step <= 1 && <button type="button" onClick={reconnectRoom} style={btn('#007bff')}>Join room {sanitizeRoom(roomId)}</button>}
            {step <= 1 && !streaming && <button type="button" onClick={startCamera} style={btn('#28a745')}>Start camera</button>}
            {step === 2 && streaming && !calibration && <button type="button" onClick={() => setStatus('Tap the four outer corners on the video now.')} style={btn('#fd7e14')}>Calibrate on video ↓</button>}
            {step === 3 && <button type="button" onClick={captureBaseline} disabled={!streaming} style={{ ...btn('#20c997'), opacity: streaming ? 1 : 0.5 }}>Capture baseline</button>}
            {step === 4 && <button type="button" onClick={runDetect} disabled={!streaming || !calibration || detectBusy} style={{ ...btn('#0d6efd'), opacity: streaming && calibration ? 1 : 0.5 }}>{detectBusy ? 'Detecting…' : 'Detect now'}</button>}
            {step >= 5 && <button type="button" onClick={confirmVisit} disabled={!canConfirm || confirmBusy} style={{ ...btn('#22c55e'), opacity: !canConfirm || confirmBusy ? 0.55 : 1 }}>{confirmBusy ? 'Sending…' : 'Confirm turn'}</button>}
            <button type="button" onClick={() => dismissWizard(false)} style={btn('#3a4553')}>Hide for now</button>
            <button type="button" onClick={() => dismissWizard(true)} style={btn('#1a2330')}>Don't show again</button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={shareCameraLink}
        style={{
          ...btn('#0d6efd'),
          width: '100%',
          padding: '18px 16px',
          fontSize: 18,
          marginBottom: 10,
          boxShadow: '0 8px 22px rgba(13,110,253,0.28)',
        }}
      >
        Share / copy this camera link{shareNote ? ` · ${shareNote}` : ''}
      </button>

      <div style={{ background: '#12161c', border: '1px solid #1e2a36', borderRadius: 12, padding: 12, marginBottom: 10, fontSize: 13, color: '#b8c0cc', lineHeight: 1.45 }}>
        <strong style={{ color: '#e8eef6' }}>Setup checklist</strong>
        <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
          {steps.map((s) => (
            <li key={s.n} style={{ color: step >= s.n ? '#8fbc8f' : '#5a6570', fontWeight: step === s.n ? 700 : 400 }}>
              {s.t}
            </li>
          ))}
        </ol>
        <div style={{ marginTop: 6, color: '#8a93a0' }}>
          <strong style={{ color: '#c5ced8' }}>Baseline tip:</strong> empty board, same phone angle/light as throws. Recapture after every pull.
          {calSavedNote ? <> · <span style={{ color: '#8fbc8f' }}>{calSavedNote}</span></> : null}
        </div>
        <button
          type="button"
          onClick={() => setShowIosTips((v) => !v)}
          style={{ marginTop: 10, ...btn('#1a2330'), border: '1px solid #2a3a4d', width: '100%', fontSize: 14 }}
        >
          {showIosTips ? 'Hide' : 'Show'} iPhone / spare-phone install tips
        </button>
        {!wizardOpen && (
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            style={{ marginTop: 8, ...btn('#1a2330'), border: '1px solid #2a3a4d', width: '100%', fontSize: 14 }}
          >
            Show first-run wizard again
          </button>
        )}
        {showIosTips && (
          <div style={{ marginTop: 10, padding: 10, background: '#0d1117', borderRadius: 8, border: '1px solid #243044', color: '#9fb0c3', fontSize: 13 }}>
            <div style={{ color: '#dce6f2', fontWeight: 700, marginBottom: 6 }}>
              {isIos() ? 'This looks like iOS — use Safari' : 'Spare phone (iPhone or Android)'}
            </div>
            <ol style={{ margin: '0 0 0 18px', padding: 0 }}>
              <li>Open this page on the <strong style={{ color: '#e8eef6' }}>HTTPS</strong> link (sslip.io), not plain http://IP.</li>
              <li><strong style={{ color: '#e8eef6' }}>iPhone:</strong> use <strong>Safari</strong> (Chrome on iOS cannot Add to Home Screen reliably). Tap Share → <strong>Add to Home Screen</strong> → open the icon, then Start camera.</li>
              <li><strong style={{ color: '#e8eef6' }}>Android:</strong> Chrome menu → Install app / Add to Home screen, or keep the tab.</li>
              <li>Allow camera when prompted. Keep the phone awake / screen on while scoring.</li>
              <li>Same Wi‑Fi as the iPad. Room code must match (default HOME).</li>
              <li>Calibration is saved <em>on this phone</em> per room — next session should skip re-tapping corners unless you move the tripod.</li>
            </ol>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        <label style={{ fontSize: 14, color: '#aaa' }}>Room</label>
        <input
          value={roomId}
          onChange={(e) => setRoomId(phoneRoomDraft(e.target.value))}
          style={{ width: 100, padding: '10px 12px', borderRadius: 8, border: 'none', background: '#1e1e1e', color: '#fff', fontWeight: 700, letterSpacing: 1 }}
        />
        <button onClick={reconnectRoom} style={btn('#007bff')}>Join</button>
        {!streaming ? (
          <button onClick={startCamera} style={btn('#28a745')}>Start camera</button>
        ) : (
          <button onClick={stopCamera} style={btn('#6c757d')}>Stop camera</button>
        )}
        <button
          onClick={() => {
            setCalibrating(true);
            setCalPoints([]);
            setCalibration(null);
            saveCal(joinedRoom || roomId, null);
            setCalSavedNote('');
            getSocket().emit('calibration:save', { roomId: joinedRoom || roomId, calibration: null });
            setStatus('Recalibrate: tap 4 outer corners of the scoring wire.');
            setStep(2);
          }}
          style={btn('#fd7e14')}
        >
          Recalibrate
        </button>
        <button onClick={captureBaseline} disabled={!streaming} style={{ ...btn('#20c997'), opacity: streaming ? 1 : 0.5 }}>
          Capture baseline
        </button>
        <button onClick={runDetect} disabled={!streaming || !calibration || detectBusy} style={{ ...btn('#0d6efd'), opacity: streaming && calibration ? 1 : 0.5 }}>
          {detectBusy ? 'Detecting…' : 'Detect now'}
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#ccc' }}>
          <input type="checkbox" checked={autoDetect} onChange={(e) => setAutoDetect(e.target.checked)} />
          Auto after settle
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#ccc' }}>
          <input type="checkbox" checked={autoBaseline} onChange={(e) => setAutoBaseline(e.target.checked)} />
          Auto baseline after confirm
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#ccc' }}>
          <input type="checkbox" checked={rejectShaky} onChange={(e) => setRejectShaky(e.target.checked)} />
          Reject shaky frames
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#ccc' }} title="Saves last baseline + frame on this phone only (IndexedDB). Never uploaded.">
          <input type="checkbox" checked={captureDebug} onChange={(e) => setCaptureDebug(e.target.checked)} />
          Capture debug (local)
        </label>
        <button
          type="button"
          onClick={async () => {
            const pair = await loadDebugPair();
            if (!pair || (!pair.baseline && !pair.frame)) {
              setDebugNote('No local debug pair yet — enable Capture debug and Detect once.');
              return;
            }
            const stamp = new Date(pair.savedAt || Date.now()).toISOString().replace(/[:.]/g, '-');
            const items = [];
            if (pair.baseline) items.push({ dataUrl: pair.baseline, filename: `astra-debug-baseline-${stamp}.jpg` });
            if (pair.frame) items.push({ dataUrl: pair.frame, filename: `astra-debug-frame-${stamp}.jpg` });
            try {
              const meta = {
                savedAt: pair.savedAt,
                roomId: pair.roomId,
                engine: pair.engine,
                calibration: pair.calibration,
                darts: pair.darts,
                diag: pair.diag,
                notes: pair.notes,
                rejected: pair.rejected || false,
              };
              const blob = new Blob([JSON.stringify(meta, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              items.push({ dataUrl: url, filename: `astra-debug-meta-${stamp}.json` });
              setTimeout(() => URL.revokeObjectURL(url), 8000);
            } catch (_) {}
            downloadDataUrlStaggered(items, 500);
            setDebugNote(`Downloading ${items.length} file(s) (staggered for Safari) — this phone only.`);
          }}
          style={{ ...btn('#495057'), fontSize: 13, padding: '8px 10px' }}
        >
          Download last debug
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#ccc' }}>
          <input type="checkbox" checked={hapticsOn} onChange={(e) => setHapticsOn(e.target.checked)} />
          Haptics
        </label>
        <select value={prefer} onChange={(e) => setPrefer(e.target.value)} style={{ padding: 8, borderRadius: 8, background: '#1e1e1e', color: '#fff', border: '1px solid #333' }}>
          <option value="auto">Engine: auto</option>
          <option value="hybrid">Engine: hybrid</option>
          <option value="onnx">Engine: onnx</option>
          <option value="opencv">Engine: opencv</option>
        </select>
      </div>

      {!secure && (
        <div style={{ background: '#4a1c1c', border: '1px solid #dc3545', padding: 12, borderRadius: 8, marginBottom: 10, fontSize: 14, lineHeight: 1.45 }}>
          <strong>Camera will not start on this address.</strong> You opened <code>{typeof window !== 'undefined' ? window.location.origin : ''}</code>, which is not HTTPS.
          Use <a href={`${CAMERA_HTTPS}/camera?room=${sanitizeRoom(joinedRoom || roomId)}`} style={{ color: '#ffd6d6' }}>{CAMERA_HTTPS}/camera?room={sanitizeRoom(joinedRoom || roomId)}</a>.
          Plain <code>http://IP:4002</code> is blocked by the phone.
        </div>
      )}
      {camError && (
        <div style={{ background: '#4a1c1c', border: '1px solid #dc3545', color: '#ffd0d0', padding: 12, borderRadius: 8, marginBottom: 8, fontSize: 14, lineHeight: 1.45 }}>
          {camError}
        </div>
      )}
      {error && <div style={{ color: '#ff8a8a', marginBottom: 8 }}>{error}</div>}

      <div style={{ background: '#12161c', border: '1px solid #1e2a36', borderRadius: 12, padding: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: '#888' }}>Live match</div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <span style={{ color: socketOk ? '#28a745' : '#dc3545', fontWeight: 700 }}>
            {socketOk ? '● Linked' : '○ Reconnecting…'}
          </span>
          <span style={{ color: '#888' }}>
            {' '}· iPad {presence.scoreboardCount ? 'connected' : 'not seen'} · room {joinedRoom || roomId}
          </span>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {mode === '501' ? (
            <>
              501 · {active501?.name || '—'} to throw · <span style={{ color: '#4dabf7' }}>{active501?.currentScore ?? '—'}</span>
            </>
          ) : mode === 'shanghai' ? (
            <>
              Shanghai · {activeSh?.name || '—'} · target{' '}
              <span style={{ color: '#4dabf7' }}>{room?.shRound?.label || '—'}</span> · score{' '}
              <span style={{ color: '#4dabf7' }}>{activeSh?.total ?? '—'}</span>
            </>
          ) : mode === 'golf' ? (
            <>
              Golf · hole <span style={{ color: '#fd7e14' }}>{room?.currentHole ?? '—'}</span>
              {' '}par {golfPar} · {activeGolf?.name || '—'} on the tee
              {room?.golfCourseName ? <> · {room.golfCourseName}</> : null}
            </>
          ) : (
            <>Waiting for iPad to start <strong>501</strong>, <strong>Shanghai</strong>, or <strong>Golf</strong> in room <span style={{ color: '#4dabf7' }}>{joinedRoom || roomId}</span></>
          )}
        </div>
        <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>{status}</div>
        <div style={{ fontSize: 12, color: '#9aa', marginTop: 4 }}>{confirmHint}</div>
        <div style={{ fontSize: 12, color: detectTimedOut ? '#fbbf24' : '#8fbc8f', marginTop: 6 }}>
          Auto-detect: {engineLabel} · {detectInfo}
          {baselineDataUrl ? ' · baseline ✓' : ' · no baseline'}
          {autoDetect ? ` · motion ${lastMotion}` : ''}
          {onnxPresent ? ' · ONNX model loaded' : ' · ONNX absent (OpenCV ok)'}
        </div>
        <div style={{ fontSize: 11, color: '#6a7a8a', marginTop: 4, fontFamily: 'ui-monospace, monospace', lineHeight: 1.45 }}>
          {lastDiag ? (
            <>
              <div>Calibrated conf (fills slots if ≥ 0.40): [{(lastDiag.confs || []).map((c) => (typeof c === 'number' ? c.toFixed(2) : c)).join(', ') || '—'}] · {lastDiag.count} tip(s) · {lastDiag.engine}{lastDiag.note ? ` · ${lastDiag.note}` : ''}</div>
              {(lastDiag.rawConfs || []).length > 0 && (
                <div>Raw model conf (before calibration, not used to fill): [{(lastDiag.rawConfs || []).map((c) => (typeof c === 'number' ? c.toFixed(3) : c)).join(', ')}]</div>
              )}
            </>
          ) : 'Last detect: —'}
        </div>
        {debugNote ? (
          <div style={{ fontSize: 11, color: '#7dd3c7', marginTop: 4 }}>{debugNote}</div>
        ) : null}
        {mode === 'shanghai' && (
          <div style={{ fontSize: 13, color: '#9ec5fe', marginTop: 6 }}>
            Mapped kinds: {shKindsPreview.length ? shKindsPreview.join(' · ') : '(empty → miss / half on confirm)'}
          </div>
        )}
      </div>

      <div style={{ position: 'relative', width: '100%', maxWidth: 720, margin: '0 auto', background: '#000', borderRadius: 12, overflow: 'hidden', aspectRatio: '4 / 3' }}>
        <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        <div
          ref={overlayRef}
          onClick={onOverlayClick}
          style={{ position: 'absolute', inset: 0, cursor: 'crosshair' }}
        >
          {calibration && (
            <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <circle
                cx={`${(calibration.cx / (videoRef.current?.videoWidth || 1)) * 100}%`}
                cy={`${(calibration.cy / (videoRef.current?.videoHeight || 1)) * 100}%`}
                r={`${(calibration.r / (videoRef.current?.videoWidth || 1)) * 100}%`}
                fill="none"
                stroke="#28a745"
                strokeWidth="2"
                strokeDasharray="6 4"
              />
            </svg>
          )}
          {visit.map((d, i) =>
            d && d.x != null ? (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${(d.x / (videoRef.current?.videoWidth || 1)) * 100}%`,
                  top: `${(d.y / (videoRef.current?.videoHeight || 1)) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  background: d.source === 'cv' ? '#20c997' : '#007bff',
                  color: '#fff',
                  borderRadius: 20,
                  padding: '2px 8px',
                  fontSize: 12,
                  fontWeight: 700,
                  pointerEvents: 'none',
                }}
              >
                {i + 1}:{formatDart(d)}
                {d.source === 'cv' ? `·AI ${Math.round((d.confidence || 0) * 100)}%` : ''}
              </div>
            ) : null
          )}
        </div>
        {!streaming && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: 18, background: '#111' }}>
            Camera preview
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
        {[0, 1, 2].map((i) => (
          <button
            key={i}
            onClick={() => setEditIndex(i)}
            style={{
              padding: 14,
              borderRadius: 10,
              border: editIndex === i ? '3px solid #28a745' : '3px solid #333',
              background: visit[i] ? '#1a3a5a' : '#1e1e1e',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 12, color: '#888' }}>Dart {i + 1}{editIndex === i ? ' · editing' : ''}</div>
            <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'monospace' }}>{formatDart(visit[i])}</div>
            <div style={{ fontSize: 12, color: '#aaa' }}>
              {visit[i]
                ? `${visit[i].value} pts · ${visit[i].source || 'tap'}${visit[i].confidence != null && visit[i].source === 'cv' ? ` · ${Math.round(visit[i].confidence * 100)}%` : ''}`
                : 'tap / auto'}
            </div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto', alignItems: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'monospace', minWidth: 90, color: '#e8eef6' }}>{total}</div>
        <button onClick={setMiss} style={btn('#3a4553')}>Miss</button>
        <button onClick={clearVisit} style={btn('#3a4553')}>Clear visit</button>
        <button onClick={undoMatch} style={btn('#1f6feb')}>Undo last turn</button>
      </div>


      {mode === '501' && active501 && (
        <div style={{ marginTop: 12, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto', background: '#101820', border: '1px solid #2a4a3a', borderRadius: 12, padding: 12 }}>
          <div style={{ fontWeight: 800, color: '#8fbc8f', marginBottom: 6 }}>Checkout helper · {active501.name} · {active501.currentScore}</div>
          {(() => {
            const routes = getCheckoutRoutes(active501.currentScore);
            if (!routes.length) return <div style={{ color: '#888', fontSize: 13 }}>No checkout in range (or bogey number) — keep scoring down.</div>;
            return (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#28a745' }}>{routes[0]}</div>
                {routes[1] && <div style={{ fontSize: 16, fontWeight: 700, color: '#4dabf7', marginTop: 4 }}>{routes[1]}</div>}
              </div>
            );
          })()}
        </div>
      )}

      <div style={{ marginTop: 12, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto', background: '#1a1420', border: '1px solid #6f42c1', borderRadius: 12, padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ fontWeight: 800, color: '#c4b5fd' }}>Practice · throw for bull</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#ccc' }}>
            <input type="checkbox" checked={practiceOn} onChange={(e) => setPracticeOn(e.target.checked)} />
            Record bull practice
          </label>
        </div>
        <div style={{ fontSize: 13, color: '#aaa', marginTop: 6, lineHeight: 1.4 }}>
          Local only on this phone — tap tips or auto-detect as usual. DB = bullseye, SB = outer bull, else miss.
        </div>
        {practiceOn && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 800 }}>T {practiceStats.throws}</span>
            <span style={{ color: '#22c55e', fontWeight: 700 }}>DB {practiceStats.db}</span>
            <span style={{ color: '#fbbf24', fontWeight: 700 }}>SB {practiceStats.sb}</span>
            <span style={{ color: '#f87171', fontWeight: 700 }}>Miss {practiceStats.miss}</span>
            <button type="button" onClick={() => setPracticeStats({ throws: 0, db: 0, sb: 0, miss: 0 })} style={btn('#3a4553')}>Reset</button>
          </div>
        )}
      </div>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          marginTop: 14,
          maxWidth: 720,
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '10px 0 6px',
          background: 'linear-gradient(180deg, rgba(11,11,11,0) 0%, #0b0b0b 28%)',
        }}
      >
        <button
          onClick={confirmVisit}
          disabled={!canConfirm || confirmBusy}
          style={{
            ...btn(canConfirm && !confirmBusy ? '#22c55e' : '#2a3340'),
            opacity: !canConfirm || confirmBusy ? 0.55 : 1,
            width: '100%',
            padding: '22px 16px',
            fontSize: 22,
            borderRadius: 14,
            boxShadow: canConfirm && !confirmBusy ? '0 8px 24px rgba(34,197,94,0.25)' : 'none',
            border: '1px solid #1b4332',
          }}
        >
          {confirmBusy ? 'Sending…' : confirmLabel}
        </button>
        <div style={{ fontSize: 12, color: '#7a8796', textAlign: 'center', marginTop: 6 }}>{confirmHint}</div>
      </div>

      {mode === 'golf' && (
        <div style={{ marginTop: 14, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto', background: '#1a140c', border: '1px solid #fd7e14', borderRadius: 12, padding: 12 }}>
          <div style={{ fontWeight: 800, color: '#fd7e14', marginBottom: 8 }}>
            Golf hole sync · {activeGolf?.name || '—'} · Hole {room?.currentHole ?? '—'} · Par {golfPar}
          </div>
          <div style={{ fontSize: 13, color: '#bbb', marginBottom: 10 }}>
            Camera tips do not map to Golf strokes. Tap the hole result — iPad updates live (same as scoreboard buttons).
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {GOLF_RESULTS.map((r) => {
              const ok = golfPar + r.relativeScore > 0 && !golfBusy;
              return (
                <button
                  key={r.label}
                  disabled={!ok}
                  onClick={() => ok && confirmGolf(r.relativeScore)}
                  style={{
                    ...btn(ok ? r.color : '#333'),
                    color: r.text || '#fff',
                    opacity: ok ? 1 : 0.4,
                    cursor: ok ? 'pointer' : 'not-allowed',
                  }}
                >
                  {golfBusy ? 'Sending…' : r.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 13, color: '#666', maxWidth: 720, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.45 }}>
        <strong style={{ color: '#aaa' }}>2-tap fix:</strong> tap a dart slot (highlights), then tap the correct spot on the board (or Miss).
        Green markers = auto-detect (shows confidence %); blue = manual tap.
        <strong style={{ color: '#aaa' }}> Confirm</strong> (501 / Shanghai) sends the visit to the iPad.
        <strong style={{ color: '#aaa' }}> Golf</strong> uses the hole-result buttons. Auto baseline refreshes ~1.4s after confirm if enabled.
      </div>
    </div>
  );
}
